// ClipGStreamViewer — 全 WebGPU 即時 4DGS viewer 的 React 包裝。
// 移植自 4dgs-clipgstream_viewer/gs-rt.js + index.html：
//   decoder(共用 device) 預解碼幀成 GPU buffer → renderer 即時 decode+sort+raster。
// 資料集改由 dataUrl prop 指定（原版靠 http server 目錄列表的 picker 不適用 SPA）；XR 未移植。
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { createDecoder, createSharedDevice, setAssetVersion } from './decoder.js'
import { createRenderer } from './renderer.js'

const MAX_GAUSSIANS = 5_000_000

// frames 支援 "0-9"（範圍）、混合 "0-9,20,30-35"，或單純逗號列表（同 gs-rt.js）
function parseFrames(str) {
  const out = []
  for (const tok of String(str).split(',')) {
    const t = tok.trim(); if (!t) continue
    const m = t.match(/^(\d+)\s*-\s*(\d+)$/)
    if (m) { for (let i = Math.min(+m[1], +m[2]); i <= Math.max(+m[1], +m[2]); i++) out.push(i) }
    else { const n = parseInt(t); if (!isNaN(n)) out.push(n) }
  }
  return out
}

// --- 最小 mat4（column-major，配 WGSL）---
function perspectiveZO(fovy, aspect, near, far) {
  const f = 1 / Math.tan(fovy / 2), nf = 1 / (near - far)
  return new Float32Array([f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, far * nf, -1, 0, 0, far * near * nf, 0])
}
function lookAt(eye, ctr, up) {
  let z0 = eye[0] - ctr[0], z1 = eye[1] - ctr[1], z2 = eye[2] - ctr[2]
  let zl = 1 / Math.hypot(z0, z1, z2); z0 *= zl; z1 *= zl; z2 *= zl
  let x0 = up[1] * z2 - up[2] * z1, x1 = up[2] * z0 - up[0] * z2, x2 = up[0] * z1 - up[1] * z0
  let xl = Math.hypot(x0, x1, x2) || 1; xl = 1 / xl; x0 *= xl; x1 *= xl; x2 *= xl
  let y0 = z1 * x2 - z2 * x1, y1 = z2 * x0 - z0 * x2, y2 = z0 * x1 - z1 * x0
  return new Float32Array([x0, y0, z0, 0, x1, y1, z1, 0, x2, y2, z2, 0,
    -(x0 * eye[0] + x1 * eye[1] + x2 * eye[2]), -(y0 * eye[0] + y1 * eye[1] + y2 * eye[2]), -(z0 * eye[0] + z1 * eye[1] + z2 * eye[2]), 1])
}

// --- orbit 相機 ↔ position/quaternion 互轉（與 DualgsViewer 的介面一致：
//     position = [x,y,z]、quaternion = {w,x,y,z}、forward = quat * (0,0,-1)）---
function rotateVecByQuat(v, q) {
  const qw = q.w ?? 1, qx = q.x ?? 0, qy = q.y ?? 0, qz = q.z ?? 0
  const tx = 2 * (qy * v[2] - qz * v[1]), ty = 2 * (qz * v[0] - qx * v[2]), tz = 2 * (qx * v[1] - qy * v[0])
  return [
    v[0] + qw * tx + qy * tz - qz * ty,
    v[1] + qw * ty + qz * tx - qx * tz,
    v[2] + qw * tz + qx * ty - qy * tx,
  ]
}

// 由相機世界座標基底（column: right / up / back）轉四元數
function basisToQuat(x, y, z) {
  const m00 = x[0], m01 = y[0], m02 = z[0]
  const m10 = x[1], m11 = y[1], m12 = z[1]
  const m20 = x[2], m21 = y[2], m22 = z[2]
  const tr = m00 + m11 + m22
  let w, qx, qy, qz
  if (tr > 0) {
    const s = Math.sqrt(tr + 1) * 2
    w = s / 4; qx = (m21 - m12) / s; qy = (m02 - m20) / s; qz = (m10 - m01) / s
  } else if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1 + m00 - m11 - m22) * 2
    w = (m21 - m12) / s; qx = s / 4; qy = (m01 + m10) / s; qz = (m02 + m20) / s
  } else if (m11 > m22) {
    const s = Math.sqrt(1 + m11 - m00 - m22) * 2
    w = (m02 - m20) / s; qx = (m01 + m10) / s; qy = s / 4; qz = (m12 + m21) / s
  } else {
    const s = Math.sqrt(1 + m22 - m00 - m11) * 2
    w = (m10 - m01) / s; qx = (m02 + m20) / s; qy = (m12 + m21) / s; qz = s / 4
  }
  return { w, x: qx, y: qy, z: qz }
}

// orbit 參數 → 相機 pose（up=[0,-1,0]，與 lookAt / GS3D 慣例一致）
function orbitToPose({ az, el, radius, ctr }) {
  const eye = [
    ctr[0] + radius * Math.cos(el) * Math.sin(az),
    ctr[1] + radius * Math.sin(el),
    ctr[2] + radius * Math.cos(el) * Math.cos(az),
  ]
  // back(z) = normalize(eye-ctr)；right(x) = up × z（up=[0,-1,0]）；up(y) = z × x
  const z = [Math.cos(el) * Math.sin(az), Math.sin(el), Math.cos(el) * Math.cos(az)]
  let x = [-z[2], 0, z[0]]
  const xl = Math.hypot(x[0], x[1], x[2]) || 1
  x = [x[0] / xl, x[1] / xl, x[2] / xl]
  const y = [z[1] * x[2] - z[2] * x[1], z[2] * x[0] - z[0] * x[2], z[0] * x[1] - z[1] * x[0]]
  return { eye, quaternion: basisToQuat(x, y, z) }
}

const ClipGStreamViewer = forwardRef(function ClipGStreamViewer({
  className,
  dataUrl,             // 資料集根目錄 URL（內含 manifest.json / base/ / clipNNN/…）
  gframes = null,      // 要播放的 global frames："0-9,20" 或 [0,1,2]；null = manifest 全部 clip
  fps: fpsProp = 60,
  keep = 0.05,         // opacity 門檻（低於即剔除）
  budgetGB = 26,       // 幀 buffer 顯存預算（GB）
  audioUrl,            // 預設 `${dataUrl}/audio.mp3`（有就播、沒有就略過）；傳 '' 停用
  showUi = true,
  showFps = false,
  onCanvasReady = null,  // (canvas) — WebGPU canvas 掛載後回呼一次
  cameraPosition = null,    // [x, y, z] — 外部控制的相機位置
  cameraQuaternion = null,  // { w, x, y, z }
  onCameraChange = null,    // (position, quaternion) — 使用者拖曳/縮放/平移時回呼
}, ref) {
  const containerRef = useRef(null)
  const canvasRef = useRef(null)
  const framesRef = useRef([])
  const rendererRef = useRef(null)
  const audioRef = useRef(null)
  const hasAudioRef = useRef(false)
  const playingRef = useRef(true)
  const curRef = useRef(0)
  const shownRef = useRef(-1)
  const lastSwapRef = useRef(0)
  // orbit 相機（up=[0,-1,0] 與 GS3D 一致）；左鍵旋轉、右鍵平移、滾輪縮放。
  // sceneCtr = 資料的框景中心（near/far 用它算，與視角控制的 ctr 解耦——
  // 外部相機/平移之後 ctr 不再代表模型位置）。
  const camRef = useRef({ az: 0, el: 0.15, radius: 1.5, ctr: [0, 0, 0], size: 1, sceneCtr: null })
  const fpsRef = useRef(fpsProp)
  const onCanvasReadyRef = useRef(onCanvasReady)
  onCanvasReadyRef.current = onCanvasReady
  const onCameraChangeRef = useRef(onCameraChange)
  onCameraChangeRef.current = onCameraChange
  // 外部相機曾被套用過 → 預解碼完成後的自動框景不要蓋掉它
  const externalCamRef = useRef(false)
  // 錄影時外部指定的固定 backing-store 尺寸（設了就讓 ResizeObserver 不要覆蓋，
  // 這樣 VideoFrame(canvas) 尺寸剛好等於 encoder 解析度，不會被拉伸變形）
  const forcedSizeRef = useRef(null)

  const [isLoading, setIsLoading] = useState(true)
  const [status, setStatus] = useState('loading…')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(null)
  const [gpuInfo, setGpuInfo] = useState(null)   // { text, sw }
  const [isPlaying, setIsPlaying] = useState(true)
  const [currentFrame, setCurrentFrame] = useState(0)
  const [totalFrames, setTotalFrames] = useState(0)
  const [fps, setFps] = useState(0)
  const [hasAudio, setHasAudio] = useState(false)
  const [isMuted, setIsMuted] = useState(false)

  useEffect(() => { fpsRef.current = fpsProp > 0 ? fpsProp : 24 }, [fpsProp])

  // 閒置自動淡出控制列（同 DualgsViewer 行為）
  const [uiVisible, setUiVisible] = useState(true)
  const idleTimerRef = useRef(null)
  const scheduleHideUi = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    idleTimerRef.current = setTimeout(() => setUiVisible(false), 1500)
  }, [])
  const handleViewerMouseMove = useCallback(() => {
    setUiVisible(true)
    scheduleHideUi()
  }, [scheduleHideUi])
  useEffect(() => {
    scheduleHideUi()
    return () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current) }
  }, [scheduleHideUi])

  // 使用者操作相機後，把目前 pose 以 DualgsViewer 相同的格式往上回報
  const emitCameraChange = useCallback(() => {
    const cb = onCameraChangeRef.current
    if (!cb) return
    const { eye, quaternion } = orbitToPose(camRef.current)
    cb([eye[0], eye[1], eye[2]], quaternion)
  }, [])

  const syncAudio = useCallback(() => {
    const audio = audioRef.current
    if (audio && hasAudioRef.current) {
      try { audio.currentTime = curRef.current / Math.max(0.1, fpsRef.current) } catch (e) { /* not ready */ }
    }
  }, [])

  // ── Play / Pause ──────────────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const next = !playingRef.current
    playingRef.current = next
    setIsPlaying(next)
    const audio = audioRef.current
    if (audio) {
      if (next) { syncAudio(); audio.play().catch(() => {}) }
      else audio.pause()
    }
  }, [syncAudio])

  // ── Restart（從第 1 幀重播）──────────────────────────────────────────────
  const handleRestart = useCallback(() => {
    curRef.current = 0
    shownRef.current = -1
    lastSwapRef.current = performance.now()
    setCurrentFrame(0)
    syncAudio()
    const audio = audioRef.current
    if (audio && playingRef.current) audio.play().catch(() => {})
  }, [syncAudio])

  // ── Timeline seek ─────────────────────────────────────────────────────────
  const handleTimelineClick = useCallback((e) => {
    const total = framesRef.current.length
    if (!total) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const index = Math.min(total - 1, Math.floor(ratio * total))
    curRef.current = index
    shownRef.current = -1
    lastSwapRef.current = performance.now()
    setCurrentFrame(index)
    syncAudio()
  }, [syncAudio])

  // ── Mute / Unmute ─────────────────────────────────────────────────────────
  const toggleMute = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.muted = !audio.muted
    setIsMuted(audio.muted)
    if (playingRef.current && audio.paused) { syncAudio(); audio.play().catch(() => {}) }
  }, [syncAudio])

  // 把 canvas backing store 鎖成固定輸出尺寸（外部錄影用，讓 VideoFrame(canvas)
  // 剛好等於 encoder 設定的解析度，避免長寬比被非等比拉伸變形）。WebGPU 的
  // getCurrentTexture() 會自動跟著 canvas.width/height，投影 aspect 也是用它算，
  // 所以只要改 backing store 尺寸即可（同 DualgsViewer.setRenderSize 的作用）。
  const setRenderSize = useCallback((width, height) => {
    const canvas = canvasRef.current
    if (!canvas) return
    if (!forcedSizeRef.current) {
      forcedSizeRef.current = { origWidth: canvas.width, origHeight: canvas.height }
    }
    canvas.width = Math.max(1, Math.round(width))
    canvas.height = Math.max(1, Math.round(height))
  }, [])

  const clearRenderSize = useCallback(() => {
    const canvas = canvasRef.current
    const saved = forcedSizeRef.current
    forcedSizeRef.current = null
    if (!canvas) return
    if (saved) {
      // 先還原成鎖定前的尺寸；下一次容器 resize 會再依 dpr 重算
      canvas.width = saved.origWidth
      canvas.height = saved.origHeight
    }
    // 立即依目前容器尺寸還原（避免停在錄影解析度直到下次 resize）
    const container = containerRef.current
    if (container) {
      const rect = container.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
        canvas.width = Math.max(1, Math.round(rect.width * dpr))
        canvas.height = Math.max(1, Math.round(rect.height * dpr))
      }
    }
  }, [])

  useImperativeHandle(ref, () => ({
    getCanvas: () => canvasRef.current,
    togglePlay,
    restart: handleRestart,
    setRenderSize,
    clearRenderSize,
  }), [togglePlay, handleRestart, setRenderSize, clearRenderSize])

  // gframes 是陣列時 join 成字串，讓 effect 依值而非參考重跑
  const gframesKey = Array.isArray(gframes) ? gframes.join(',') : (gframes ?? '')

  // ── WebGPU 場景：載入 manifest → 預解碼全部幀 → rAF 播放迴圈 ────────────
  useEffect(() => {
    if (!dataUrl || !canvasRef.current || !containerRef.current) return

    // StrictMode dev 雙掛載：第一次掛載的預解碼是 async 的，cleanup 之後才 resolve。
    // 沒這個 flag 的話舊迴圈會疊在新迴圈上（同 DualgsViewer 的處理）。
    let disposed = false
    let animId = null
    let device = null
    const frames = []
    const canvas = canvasRef.current
    const container = containerRef.current

    const log = (m) => { console.log(m); if (!disposed) setStatus(m) }

    setIsLoading(true)
    setError(null)
    setProgress(0)
    curRef.current = 0
    shownRef.current = -1
    framesRef.current = []

    // Resize — 跟著容器尺寸（原版跟 window），dpr 上限 1.5 同 gs-rt
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
    const applySize = (width, height) => {
      // 錄影中被鎖定成固定匯出解析度時，忽略容器尺寸變化
      if (forcedSizeRef.current) return
      canvas.width = Math.max(1, Math.round(width * dpr))
      canvas.height = Math.max(1, Math.round(height * dpr))
    }
    const rect = container.getBoundingClientRect()
    applySize(rect.width, rect.height)
    const resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      if (width > 0 && height > 0) applySize(width, height)
    })
    resizeObserver.observe(container)

    // orbit 相機輸入：左鍵旋轉、右鍵平移、滾輪縮放
    let drag = false, btn = 0, px = 0, py = 0
    const onContextMenu = (e) => e.preventDefault()   // 右鍵不跳系統選單
    const onPointerDown = (e) => { drag = true; btn = e.button; px = e.clientX; py = e.clientY; canvas.setPointerCapture(e.pointerId) }
    const onPointerUp = () => { drag = false }
    const onPointerMove = (e) => {
      if (!drag) return
      const cam = camRef.current
      const dx = e.clientX - px, dy = e.clientY - py; px = e.clientX; py = e.clientY
      if (btn === 2) {   // 右鍵：平移（移動 look-at 中心 ctr，eye 每幀由 ctr 推出所以跟著移）
        const { az, el, radius, ctr } = cam
        const fx = -Math.cos(el) * Math.sin(az), fy = -Math.sin(el), fz = -Math.cos(el) * Math.cos(az)  // forward
        let rx = fz, ry = 0, rz = -fx; const rl = Math.hypot(rx, ry, rz) || 1; rx /= rl; rz /= rl        // right=normalize(cross(fwd,[0,-1,0]))
        const ux = ry * fz - rz * fy, uy = rz * fx - rx * fz, uz = rx * fy - ry * fx                     // up=cross(right,fwd)
        const k = radius * 0.0015                                                                       // 平移速度隨縮放一致
        ctr[0] += -dx * k * rx + dy * k * ux; ctr[1] += -dx * k * ry + dy * k * uy; ctr[2] += -dx * k * rz + dy * k * uz
      } else {           // 左鍵：旋轉
        cam.az += dx * 0.005; cam.el -= dy * 0.005; cam.el = Math.max(-1.5, Math.min(1.5, cam.el))
      }
      emitCameraChange()
    }
    const onWheel = (e) => { camRef.current.radius *= Math.exp(e.deltaY * 0.001); e.preventDefault(); emitCameraChange() }
    canvas.addEventListener('contextmenu', onContextMenu)
    canvas.addEventListener('pointerdown', onPointerDown)
    canvas.addEventListener('pointerup', onPointerUp)
    canvas.addEventListener('pointermove', onPointerMove)
    canvas.addEventListener('wheel', onWheel, { passive: false })

    async function init() {
      if (!navigator.gpu) throw new Error('此瀏覽器不支援 WebGPU（請用 Chrome/Edge 113+）')
      const manifest = await (await fetch(`${dataUrl}/manifest.json`, { cache: 'no-store' })).json()
      setAssetVersion(manifest.version || '')   // 用匯出時間戳做 cache-busting
      manifest._dataRoot = dataUrl
      const CS = manifest.clip_size
      let GFRAMES = gframesKey ? parseFrames(gframesKey) : null
      if (!GFRAMES) {   // 未指定 gframes → 涵蓋 manifest 所有 clip（每個 clip 展開 CS 幀）
        GFRAMES = []
        for (const cl of manifest.clips) { const b = cl.id * CS; for (let k = 0; k < CS; k++) GFRAMES.push(b + k) }
      }

      // 共用單一 device（關鍵：不要每個 clip 開一個）
      device = await createSharedDevice(log, 512 << 20, (text, sw) => { if (!disposed) setGpuInfo({ text, sw }) })
      if (disposed) { try { device.destroy() } catch (e) {} ; return }
      const renderer = createRenderer(device, canvas, MAX_GAUSSIANS)
      rendererRef.current = renderer

      // 預解碼：逐 clip 處理，每個 clip 解完就 destroy 釋放它的參數（只留輸出的幀 buffer）。
      const BUDGET = budgetGB * 1e9
      let bytes = 0, curDec = null, curClip = -1, stopped = false, framedCam = false
      for (let i = 0; i < GFRAMES.length && !stopped; i++) {
        if (disposed) { stopped = true; break }
        const gf = GFRAMES[i], c = Math.floor(gf / CS), t = (gf % CS) / CS
        if (c !== curClip) {
          if (curDec) { curDec.destroy(); curDec = null }   // 釋放上一個 clip 的參數
          const clip = manifest.clips.find(x => x.id === c)
          if (!clip) { console.warn(`clip ${c} 未匯出，跳過 gf ${gf}`); continue }
          log(`載入 clip ${c} 並預解碼…（已快取 ${frames.length} 幀）`)
          device.pushErrorScope('out-of-memory')
          try { curDec = await createDecoder(clip, `${dataUrl}/${clip.dir}`, manifest, log, device) } catch (e) { console.warn('createDecoder 例外', e.message) }
          if (await device.popErrorScope() || !curDec) { console.warn(`載入 clip ${c} 顯存不足，停止於 ${frames.length} 幀`); stopped = true; break }
          curClip = c
          if (!framedCam) {
            // 百分位數框景（1%~99%）：忽略離群 anchor，避免單顆遠點把 size 撐爆、相機被推到超遠
            const apath = manifest.base ? `${dataUrl}/${manifest.base.dir}/anchor.f32` : `${dataUrl}/${clip.dir}/anchor.f32`
            const r = await fetch(apath); if (!r.ok) throw new Error('fetch ' + apath)
            const anchor = new Float32Array(await r.arrayBuffer()), N = anchor.length / 3
            const mn = [0, 0, 0], mx = [0, 0, 0]
            for (let d = 0; d < 3; d++) {
              const a = new Float32Array(N)
              for (let j = 0; j < N; j++) a[j] = anchor[j * 3 + d]
              a.sort()                                   // TypedArray.sort 預設為數值排序
              mn[d] = a[Math.floor(N * 0.01)]; mx[d] = a[Math.floor(N * 0.99)]
            }
            const cam = camRef.current
            cam.size = Math.max(mx[0] - mn[0], mx[1] - mn[1], mx[2] - mn[2]) || 1
            cam.sceneCtr = [(mn[0] + mx[0]) / 2, (mn[1] + mx[1]) / 2, (mn[2] + mx[2]) / 2]
            // 外部相機（cameraPosition/cameraQuaternion）已就位時只更新
            // size/sceneCtr（near/far 需要），不要把使用者/動畫指定的視角蓋掉。
            if (!externalCamRef.current) {
              cam.ctr = [...cam.sceneCtr]
              cam.radius = 1.5 * cam.size
            }
            framedCam = true
          }
        }
        log(`預解碼 gf ${gf}（clip ${c}）… ${i + 1}/${GFRAMES.length}`)
        if (!disposed) setProgress(((i + 1) / GFRAMES.length) * 100)
        device.pushErrorScope('out-of-memory')
        let res = null
        try { res = await curDec.decodeToGPU(t, keep) } catch (e) { console.warn('decode 例外', e.message) }
        if (await device.popErrorScope() || !res) { console.warn(`顯存不足，停止預解碼於 ${frames.length} 幀`); stopped = true; break }
        bytes += res.count * (res.f16 ? 9 : 17) * 4
        frames.push({ buffer: res.buffer, count: res.count, f16: res.f16, gf })
        console.log(`  gf ${gf}: ${res.count} 高斯（${res.f16 ? 'f16' : 'f32'}，累計 ${(bytes / 1e9).toFixed(1)}GB）`)
        if (bytes > BUDGET) { console.warn(`已達顯存預算 ${budgetGB}GB，停止預解碼於 ${frames.length} 幀`); stopped = true }
      }
      if (curDec) { curDec.destroy(); curDec = null }
      if (disposed) { for (const f of frames) { try { f.buffer.destroy() } catch (e) {} } ; return }
      if (frames.length === 0) throw new Error('沒有可播放的幀')

      framesRef.current = frames
      setTotalFrames(frames.length)
      setIsLoading(false)

      // 聲音：資料集資料夾內的 audio.mp3（有就播、沒有就略過）。單純跟著影片播，
      // 假設音檔 fps = 播放 fps → 第 cur 幀對應音檔時間 cur/fps。
      const resolvedAudio = audioUrl === '' ? null : (audioUrl || `${dataUrl}/audio.mp3`)
      if (resolvedAudio) {
        const audio = new Audio(resolvedAudio)
        audio.addEventListener('canplay', () => { hasAudioRef.current = true; if (!disposed) setHasAudio(true) })
        audio.addEventListener('error', () => { hasAudioRef.current = false; if (!disposed) setHasAudio(false) })
        audioRef.current = audio
        if (playingRef.current) {
          audio.play().catch(() => {
            // 瀏覽器擋未靜音自動播放 → 改靜音播，讓使用者點 🔇 開聲音
            audio.muted = true
            if (!disposed) setIsMuted(true)
            audio.play().catch(() => {})
          })
        }
      }

      if (onCanvasReadyRef.current) onCanvasReadyRef.current(canvas)

      log(`即時播放（全 WebGPU）：${frames.length} 幀`)

      // 播放迴圈（不重載，全靠 preload 的幀 buffer）
      lastSwapRef.current = performance.now()
      let prevTime = 0, avgFps = 0, ticks = 0
      const loop = (now) => {
        if (disposed) return
        animId = requestAnimationFrame(loop)
        const playFps = Math.max(0.1, fpsRef.current)
        if (playingRef.current) {
          if (now - lastSwapRef.current >= 1000 / playFps) {
            const nxt = (curRef.current + 1) % frames.length
            if (nxt === 0) {   // 循環：影片+音檔一起回頭
              const audio = audioRef.current
              if (audio && hasAudioRef.current) { try { audio.currentTime = 0; audio.play().catch(() => {}) } catch (e) {} }
            }
            curRef.current = nxt
            lastSwapRef.current = now
          }
        }
        const cur = curRef.current
        if (shownRef.current !== cur) {
          const fr = frames[cur]
          renderer.setFrame(fr.buffer, fr.count, fr.f16)
          shownRef.current = cur
          setCurrentFrame(cur)
        }
        const { az, el, radius, ctr, size, sceneCtr } = camRef.current
        const eye = [ctr[0] + radius * Math.cos(el) * Math.sin(az), ctr[1] + radius * Math.sin(el), ctr[2] + radius * Math.cos(el) * Math.cos(az)]
        const view = lookAt(eye, ctr, [0, -1, 0])
        // near/far 用「相機到場景中心的真實距離」推：外部相機/平移後 orbit
        // radius 不再等於相機到模型的距離，用 radius 算會把資料裁掉（全黑）。
        const sc = sceneCtr || ctr
        const d = Math.hypot(eye[0] - sc[0], eye[1] - sc[1], eye[2] - sc[2]) || radius
        const near = Math.max(0.01, d - size), far = d + size
        const proj = perspectiveZO(1.0, canvas.width / canvas.height, near, far)
        renderer.render(view, proj, eye, near, far)

        const delta = now - prevTime
        prevTime = now
        if (delta > 0) {
          avgFps = avgFps * 0.9 + (1000 / delta) * 0.1
          ticks++
          if (ticks % 10 === 0) setFps(Math.round(avgFps))
        }
      }
      animId = requestAnimationFrame(loop)
    }

    init().catch((e) => {
      console.error(e)
      if (!disposed) { setError(e.message); setIsLoading(false) }
    })

    // Cleanup — 停迴圈、釋放幀 buffer 與 device（VRAM 立即歸還）
    return () => {
      disposed = true
      resizeObserver.disconnect()
      if (animId !== null) cancelAnimationFrame(animId)
      canvas.removeEventListener('contextmenu', onContextMenu)
      canvas.removeEventListener('pointerdown', onPointerDown)
      canvas.removeEventListener('pointerup', onPointerUp)
      canvas.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('wheel', onWheel)
      const audio = audioRef.current
      if (audio) { audio.pause(); audio.src = ''; audioRef.current = null }
      hasAudioRef.current = false
      for (const f of framesRef.current) { try { f.buffer.destroy() } catch (e) {} }
      for (const f of frames) { try { f.buffer.destroy() } catch (e) {} }
      framesRef.current = []
      rendererRef.current = null
      if (device) { try { device.destroy() } catch (e) {} }
    }
  }, [dataUrl, gframesKey, keep, budgetGB, audioUrl])

  // ── 外部相機同步（cameraPosition / cameraQuaternion）────────────────────
  // orbit 相機沒有 roll（up 固定 [0,-1,0]），所以只取 quaternion 的 forward：
  // 沿視線方向、以目前 radius 推出 look-at 中心，再反解 az/el。與 DualgsViewer
  // 把 OrbitControls target 放到相機前方的做法對應；round-trip 自己 emit 出去的
  // pose 不會漂移。
  useEffect(() => {
    if (!cameraPosition || !cameraQuaternion) return
    const cam = camRef.current
    externalCamRef.current = true
    const eye = cameraPosition
    const fwd = rotateVecByQuat([0, 0, -1], cameraQuaternion)
    const dist = cam.radius > 0 && Number.isFinite(cam.radius) ? cam.radius : 1
    const ctr = [eye[0] + fwd[0] * dist, eye[1] + fwd[1] * dist, eye[2] + fwd[2] * dist]
    cam.ctr = ctr
    cam.radius = dist
    cam.el = Math.asin(Math.max(-1, Math.min(1, (eye[1] - ctr[1]) / dist)))
    cam.az = Math.atan2(eye[0] - ctr[0], eye[2] - ctr[2])
  }, [cameraPosition, cameraQuaternion])

  // ── Shared button style（同 DualgsViewer）─────────────────────────────────
  const ctrlBtn = {
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: 6,
    fontSize: '0.82rem',
    border: '1px solid rgba(255,255,255,0.18)',
    cursor: 'pointer',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  }

  const progressPct = totalFrames > 1 ? (currentFrame / (totalFrames - 1)) * 100 : 0
  const currentGf = framesRef.current[currentFrame]?.gf

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className={className}
      style={{ position: 'relative', width: '100%', height: '100%', background: '#000' }}
      onMouseMove={handleViewerMouseMove}
    >
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none' }} />
      </div>

      {/* Loading overlay（進度 = 預解碼幀數） */}
      {isLoading && !error && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: 'rgba(0,0,0,0.4)', pointerEvents: 'none',
        }}>
          <div style={{ color: '#fff', fontSize: 14 }}>Loading {Math.round(progress)}%</div>
          <div style={{ width: 200, height: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: '#fff', borderRadius: 3, transition: 'width 0.15s' }} />
          </div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontFamily: 'monospace', maxWidth: '80%', textAlign: 'center' }}>
            {status}
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.8)', color: '#f44', fontSize: '1.2rem', padding: 24, textAlign: 'center',
        }}>
          Error: {error}
        </div>
      )}

      {/* GPU 資訊（軟體模擬時轉紅警示） */}
      {showUi && gpuInfo && !error && (
        <div style={{
          position: 'absolute', top: 8, left: 8, zIndex: 15,
          fontSize: 12, fontFamily: 'monospace',
          background: 'rgba(0,0,0,0.55)', padding: '4px 8px', borderRadius: 4,
          color: gpuInfo.sw ? '#f55' : '#5f5',
          opacity: uiVisible ? 1 : 0, transition: 'opacity 0.3s ease',
          pointerEvents: 'none',
        }}>
          {gpuInfo.text}
        </div>
      )}

      {/* ── Controls Wrap (bottom bar，樣式同 DualgsViewer) ── */}
      {showUi && !isLoading && !error && (
        <div style={{
          position: 'absolute',
          bottom: 0, left: 0, right: 0,
          padding: '0 16px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          pointerEvents: 'none',
          opacity: uiVisible ? 1 : 0,
          transition: 'opacity 0.3s ease',
          zIndex: 10,
        }}>

          {/* Timeline */}
          <div
            onClick={handleTimelineClick}
            style={{
              position: 'relative',
              height: 20,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
          >
            {/* Track */}
            <div style={{
              position: 'absolute',
              left: 0, right: 0,
              height: 4,
              background: 'rgba(255,255,255,0.18)',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${progressPct}%`,
                background: 'rgba(255,255,255,0.75)',
                borderRadius: 2,
                transition: 'width 0.05s linear',
              }} />
            </div>

            {/* Handle */}
            <div style={{
              position: 'absolute',
              left: `${progressPct}%`,
              transform: 'translateX(-50%)',
              width: 12, height: 12,
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 0 4px rgba(0,0,0,0.5)',
              transition: 'left 0.05s linear',
              pointerEvents: 'none',
            }} />

            {/* Frame label（gf = global frame） */}
            <div style={{
              position: 'absolute',
              right: 0,
              top: -18,
              color: 'rgba(255,255,255,0.55)',
              fontSize: '0.7rem',
              fontFamily: 'monospace',
              userSelect: 'none',
            }}>
              {currentGf != null ? `gf ${currentGf} · ` : ''}{currentFrame + 1}/{totalFrames}
            </div>
          </div>

          {/* Buttons Container */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(14,14,14,0.5)',
            backdropFilter: 'blur(10px)',
            padding: '8px 12px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.1)',
            pointerEvents: 'auto',
          }}>
            <button
              onClick={togglePlay}
              title={isPlaying ? 'Pause' : 'Play'}
              style={{ ...ctrlBtn, minWidth: 36, justifyContent: 'center', fontSize: '1rem', padding: '4px 10px' }}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button
              onClick={handleRestart}
              title="從第 1 幀重播"
              style={{ ...ctrlBtn, minWidth: 36, justifyContent: 'center', fontSize: '1rem', padding: '4px 10px' }}
            >
              ⏮
            </button>

            <div style={{ flex: 1 }} />

            {hasAudio && (
              <button
                onClick={toggleMute}
                title={isMuted ? 'Unmute' : 'Mute'}
                style={{ ...ctrlBtn, minWidth: 36, justifyContent: 'center', fontSize: '1rem', padding: '4px 10px' }}
              >
                {isMuted ? '🔇' : '🔊'}
              </button>
            )}

            {showFps && (
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', minWidth: 52 }}>
                {fps} FPS
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
})

export default ClipGStreamViewer
